/**
 * Query builders (Drizzle) for vault. Returns compilable queries wrapped
 * by web in `createPowerSyncQuery` for local-first reactivity.
 */

import {
  and,
  desc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  vault,
  vault_account,
  vault_attachment,
  vault_item,
  vault_membership,
  vault_share,
} from "@boveda/powersync";

/** User account root row (0 or 1 row). */
export function getVaultAccountQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(vault_account).where(eq(vault_account.user_id, userId)).limit(1);
}

/** User personal vault(s), non-deleted. */
export function getPersonalVaultsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(vault)
    .where(and(eq(vault.owner_user_id, userId), isNull(vault.deleted_at)))
    .orderBy(desc(vault.created_at));
}

/** Live (non-deleted) items of a vault, newest first. */
export function getVaultItemsQuery(db: PowerSyncDrizzleDB, vaultId: string) {
  return db
    .select()
    .from(vault_item)
    .where(and(eq(vault_item.vault_id, vaultId), isNull(vault_item.deleted_at)))
    .orderBy(desc(vault_item.updated_at));
}

/**
 * ORGANIZATION vaults visible to this client (sync already limits them
 * to user's orgs), non-deleted.
 */
export function getOrgVaultsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(vault)
    .where(and(eq(vault.type, "organization"), isNull(vault.deleted_at)))
    .orderBy(desc(vault.created_at));
}

/** ALL my organization vault memberships (for list reactivity). */
export function getMyVaultMembershipsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(vault_membership).where(eq(vault_membership.user_id, userId));
}

/** MY membership (sealed key) in an organization vault (0 or 1 row). */
export function getMyVaultMembershipQuery(db: PowerSyncDrizzleDB, vaultId: string, userId: string) {
  return db
    .select()
    .from(vault_membership)
    .where(and(eq(vault_membership.vault_id, vaultId), eq(vault_membership.user_id, userId)))
    .limit(1);
}

/** Secrets shared WITH ME (envelopes sealed to my public key). */
export function getIncomingSharesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(vault_share)
    .where(eq(vault_share.to_user_id, userId))
    .orderBy(desc(vault_share.created_at));
}

/** Live (non-deleted) attachments of an item, newest first. */
export function getItemAttachmentsQuery(db: PowerSyncDrizzleDB, itemId: string) {
  return db
    .select()
    .from(vault_attachment)
    .where(and(eq(vault_attachment.item_id, itemId), isNull(vault_attachment.deleted_at)))
    .orderBy(desc(vault_attachment.created_at));
}

/** Soft-deleted items of a vault (trash). */
export function getVaultTrashQuery(db: PowerSyncDrizzleDB, vaultId: string) {
  return db
    .select()
    .from(vault_item)
    .where(eq(vault_item.vault_id, vaultId))
    .orderBy(desc(vault_item.updated_at));
}
